import { eventChannel, END, SagaIterator } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';
import WebSocketClient from '@clients/web-socket.client';
import { setAuthHeader } from '@clients/http.client';
import { logger } from '@utils';
import * as UserActions from '../user/user.actions';
import * as CommonActions from './common.action';
import * as MessageTypes from './common.constants';
import { PROTOCOL_VERSION } from '@services/user/user.saga';
import { enrichFamily } from '@services/family/family.actions';
import { privatRoutesPathes } from '@router/router.constants';
import { IConnectSocket } from '@app-types/websocket/common.types';
import { IAppToken } from '@app-types/websocket/user.types';
import { getAppTokenPlatform } from '@helpers/osDetection';
import { wasAppJustOpened } from '@helpers/appLifecycle';
import * as FileActions from '@services/file/file.actions';
import { getUploadInternalId } from '@services/file/upload.errors';
import { isWebSocketNetworkFailureCode } from '@services/status/maintenance-status';
import { isGmailConnectPath } from '../../integrations/gmail/gmailAttemptVault';

let watchdogTimer: NodeJS.Timeout | undefined;
let lastWrite: number | undefined;
let lastRead: number | undefined;
let currentSocketId = 0; 

let connectionRetryCount = 0;
// Not reset by onOpen, so a LOGIN error that repeats on every connection
// backs off instead of reloading the snapshot in a tight loop.
let loginErrorRetryCount = 0;

const AUTHENTICATION_TIMEOUT_MS = 30000;
const LOGIN_ERROR_CLOSE_GRACE_MS = 3000;
const CONNECTION_WARNING_STARTUP_SUPPRESSION_MS = 1000;

function watchdog(ws: WebSocketClient, emitter: any, socketId: number) {
  if (socketId !== currentSocketId) return;
  if (watchdogTimer) clearTimeout(watchdogTimer);

  const socket = ws.getSocket();
  const readyState = socket ? socket.readyState : 3;

  // Zombie check: 10s since last write + 15s since last read
  const isZombie = (lastWrite !== undefined && (Date.now() - lastWrite > 30000)) && 
                   (lastRead === undefined || (Date.now() - lastRead) > 25000);

  if (isZombie || readyState === 3) {
    ws.close();
    emitter(CommonActions.websocketDisconnected());
    emitter(CommonActions.bootstrap({}));
    return;
  }

  if (readyState === 1 && ws.hasConnection()) {
    if (lastWrite === undefined || (Date.now() - lastWrite) > 10000) {
      ws.sendJSON({});
      lastWrite = Date.now();
    }
  }

  watchdogTimer = setTimeout(() => watchdog(ws, emitter, socketId), 10000);
}


export function connectSocket(data: IConnectSocket) {
  return eventChannel((emitter) => {
    const ws = WebSocketClient.getInstance();
    let hasSentAppToken = false;
    let authenticationTimeoutTimer: NodeJS.Timeout | undefined;
    
    // Increment global session ID to invalidate old listeners
    currentSocketId++;
    const thisSessionId = currentSocketId;
    const suppressConnectionWarning = getAppTokenPlatform() === "android" &&
      wasAppJustOpened(CONNECTION_WARNING_STARTUP_SUPPRESSION_MS);
    let hasReportedConnectionFailure = false;
    let reconnectTimer: NodeJS.Timeout | undefined;
    let loginErrorTimer: NodeJS.Timeout | undefined;
    let hasCompletedLogin = false;

    const reportConnectionFailure = () => {
      if (hasReportedConnectionFailure || suppressConnectionWarning || currentSocketId !== thisSessionId) {
        return;
      }

      hasReportedConnectionFailure = true;
      emitter(UserActions.checkServerStatus(
        "Cannot connect to the server. Try turning off your WiFi if this persists."
      ));
    };

    // Initiate the connection
    let socket: WebSocket;
    try {
      socket = ws.createConnection(data.socketURL);
      // socket = ws.createConnection(data.socketURL.replace("wss://api.varannanvecka.app:443","ws://localhost:8080"));
    } catch (error) {
      logger.error("Could not create WebSocket connection:", error);
      emitter(CommonActions.websocketDisconnected());
      connectionRetryCount++;
      reconnectTimer = setTimeout(() => {
        if (thisSessionId === currentSocketId) {
          emitter(CommonActions.bootstrap({}));
        }
      }, Math.min(connectionRetryCount * 1000, 5000));

      return () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
      };
    }

    authenticationTimeoutTimer = setTimeout(() => {
      if (thisSessionId !== currentSocketId || ws.hasConnection()) return;
      console.warn("WebSocket authentication timed out, reconnecting.");
      reportConnectionFailure();
      ws.closeSocket(socket);
      emitter(CommonActions.websocketDisconnected());
      emitter(CommonActions.bootstrap({}));
    }, AUTHENTICATION_TIMEOUT_MS);

    ws.onOpen(() => {
      if (thisSessionId !== currentSocketId) return;

      // SUCCESS: Reset retry state.
      connectionRetryCount = 0;

      ws.sendJSON({
        requestId: "LOGIN",
        authorization: data.subscribeData.authorization,
        version: PROTOCOL_VERSION
      }, true);
      
      lastRead = lastWrite = undefined;
      // Stored so closing paths can cancel it before it bootstraps early.
      watchdogTimer = setTimeout(() => watchdog(ws, emitter, thisSessionId), 25000 );
    });

    ws.onMessage(event => {
      if (thisSessionId !== currentSocketId) return;
      
      let eventData: any;
      try {
        eventData = JSON.parse(event.data);
      } catch (error) {
        // A disrupted connection can deliver a truncated or non-JSON frame.
        // Drop it instead of throwing out of the socket handler, and do not
        // refresh lastRead so the watchdog can still detect a broken stream.
        logger.error("Failed to parse WebSocket message:", error);
        return;
      }

      // Only treat the socket as alive once we have a frame we can actually use.
      lastRead = Date.now();
      const { payloadTypes } = eventData;

      const hasRequestId = Array.isArray(payloadTypes) &&
        payloadTypes.indexOf(MessageTypes.commonWebSocket.special.REQUEST_ID) >= 0;
      const hasErrorMessage = Array.isArray(payloadTypes) &&
        payloadTypes.indexOf(MessageTypes.commonWebSocket.special.ERROR_MESSAGE) >= 0;
      const isAuthenticationAcknowledgement = hasRequestId && eventData.requestId === "AUTH";
      const isLoginResponse = hasRequestId && eventData.requestId === "LOGIN";
      const isLoginAuthenticationFailure = isLoginResponse && hasErrorMessage;
      const isLoginAcknowledgement = isLoginResponse && !hasErrorMessage;
      const rejectedUploadInternalId = hasErrorMessage
        ? getUploadInternalId(eventData.requestId)
        : undefined;

      if (isLoginAcknowledgement) {
        hasCompletedLogin = true;
        loginErrorRetryCount = 0;
      }

      if (isLoginAuthenticationFailure) {
        // The server also reports database errors in the login snapshot as
        // LOGIN errors, so this frame alone does not mean the token was
        // rejected. A rejected token is followed by a 1008 close, which
        // onClose turns into a logout. Otherwise reconnect.
        console.warn("WebSocket login failed on the server:", eventData.errorMsg);
        if (hasCompletedLogin || loginErrorTimer) {
          // Either work after a completed login failed and the session still
          // works, or a reconnect is already pending.
          return;
        }
        emitter(CommonActions.websocketLoginFailed());
        ws.setConnected(false);
        if (authenticationTimeoutTimer) {
          clearTimeout(authenticationTimeoutTimer);
          authenticationTimeoutTimer = undefined;
        }
        // Give the server's close time to arrive first. Closing locally right
        // away would hide a real rejection and reconnect forever.
        loginErrorTimer = setTimeout(() => {
          loginErrorTimer = undefined;
          if (thisSessionId !== currentSocketId) return;
          // The server's close is already under way. Keep onclose attached so
          // onClose can still see a rejection and log out.
          if (socket.readyState !== socket.OPEN) return;
          if (watchdogTimer) clearTimeout(watchdogTimer);
          ws.closeSocket(socket);
          emitter(CommonActions.websocketDisconnected());

          loginErrorRetryCount++;
          // Backoff: 2s, 4s, 6s, ... max 30s
          reconnectTimer = setTimeout(() => {
            if (thisSessionId === currentSocketId) {
              emitter(CommonActions.bootstrap({}));
            }
          }, Math.min(loginErrorRetryCount * 2000, 30000));
        }, LOGIN_ERROR_CLOSE_GRACE_MS);
        return;
      }

      if ((isAuthenticationAcknowledgement || isLoginAcknowledgement) && !ws.hasConnection()) {
        ws.setConnected(true);
        emitter(CommonActions.websocketAuthenticated());
        if (authenticationTimeoutTimer) {
          clearTimeout(authenticationTimeoutTimer);
          authenticationTimeoutTimer = undefined;
        }

        if (!hasSentAppToken && data.appToken && data.appToken.length > 1) {
          const platform = getAppTokenPlatform();

          if (platform) {
            const tokenData:IAppToken = {
              platform,
              appToken: data.appToken,
            };
            ws.sendJSON({
              appTokenUpdate: tokenData,
            });
          } else {
            ws.sendJSON({
              appToken: data.appToken,
            });
          }

          hasSentAppToken = true;
        }
      }

      for (const eventType of (payloadTypes || [])) {
        if (eventType === MessageTypes.commonWebSocket.special.ERROR_MESSAGE
          && rejectedUploadInternalId !== undefined) {
          emitter(FileActions.uploadSlotRejected({
            internalId: rejectedUploadInternalId,
            errorMessage: typeof eventData.errorMsg === "string" ? eventData.errorMsg : "",
          }));
          continue;
        }

        const payload = mapEventToPayload(eventType, eventData);
        if (payload) {
          emitter({ type: eventType, payload });
        }
        
        // Trigger data enrichment for snapshots
        if (eventType === MessageTypes.commonWebSocket.snapshot.FAMILY_SNAPSHOT || 
            eventType === MessageTypes.commonWebSocket.replace.CALENDAR_SETTINGS) {
          emitter(enrichFamily());
        }
      }
    });

    ws.onError(() => {
      if (thisSessionId !== currentSocketId || ws.hasConnection()) return;
      reportConnectionFailure();
    });

    ws.onClose(event => {

      console.log("WebSocket closed", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      if (thisSessionId !== currentSocketId) return;
      const wasAuthenticated = ws.hasConnection();
      ws.setConnected(false);
      if (authenticationTimeoutTimer) {
        clearTimeout(authenticationTimeoutTimer);
        authenticationTimeoutTimer = undefined;
      }
      if (loginErrorTimer) {
        clearTimeout(loginErrorTimer);
        loginErrorTimer = undefined;
      }
      if (watchdogTimer) clearTimeout(watchdogTimer);

      const isAuthError = !wasAuthenticated &&
        ((!!event.reason && /auth/i.test(event.reason)) || event.code === 1008);

      if (isAuthError) {
        console.log("WebSocket auth error, clearing the stored session.");
        connectionRetryCount = 0;
        loginErrorRetryCount = 0;
        // Keep logout as the first terminal event so disconnect handling cannot
        // race the session reset.
        emitter(UserActions.logout());
        emitter(END);
      } else {
        emitter(CommonActions.websocketDisconnected());
        // 1006 is the browser's abnormal-closure code for a lost transport.
        // Clean or application-level closes before authentication are not
        // maintenance-status triggers.
        if (!wasAuthenticated && isWebSocketNetworkFailureCode(event.code)) reportConnectionFailure();

        // FAIL: Increment retry count
        connectionRetryCount++;

        // Calculate backoff: 1s, 2s, 3s, 4s, max 5s
        const backoffDelay = Math.min(connectionRetryCount * 1000, 5000);

        // Schedule a bootstrap retry after the backoff
        reconnectTimer = setTimeout(() => {
          if (thisSessionId === currentSocketId) {
            emitter(CommonActions.bootstrap({}));
          }
        }, backoffDelay);
      }
    });

    // Cleanup logic when the channel is closed or saga is cancelled
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (authenticationTimeoutTimer) {
        clearTimeout(authenticationTimeoutTimer);
        authenticationTimeoutTimer = undefined;
      }
      if (loginErrorTimer) {
        clearTimeout(loginErrorTimer);
        loginErrorTimer = undefined;
      }
      if (thisSessionId === currentSocketId && watchdogTimer) {
        clearTimeout(watchdogTimer);
      }
      try {
        ws.closeSocket(socket);
      } catch (e) {
        logger.error("Error closing WebSocket:", e);
      }
    };
  });
}

/**
 * Mapping raw socket data to Redux actions
 */
function mapEventToPayload(type: string, data: any): any {
  const m = MessageTypes.commonWebSocket;
  switch (type) {
    case m.snapshot.CHATS_SNAPSHOT: return { chats: data.chats };
    case m.snapshot.FAMILY_SNAPSHOT: return { family: data.family };
    case m.snapshot.FAMILY_DATA: return { familyData: data.familyData };
    case m.replace.CHILD_INFO_UPDATE: return { childInfo: data.childInfo };
    case m.replace.FLOW_POST_UPDATE: return { flowPosts: data.flowPosts };
    case m.special.FLOW_POST_REMOVED: return { removedFlowPost: data.removedFlowPost };
    case m.replace.SHARED_NOTE_UPDATE: return { sharedNotes: data.sharedNotes };
    case m.snapshot.SHARED_NOTE_ORDER_SNAPSHOT: return { sharedNoteOrder: data.sharedNoteOrder };
    case m.special.SHARED_NOTE_REMOVED: return { removedNote: data.removedNote };
    case m.snapshot.SCHEDULES_SNAPSHOT: return { schedules: data.schedules };
    case m.snapshot.SUGGESTIONS_SNAPSHOT: return { suggestions: data.suggestions };
    case m.replace.FAMILY_UPDATE: return { familyUpdate: data.familyUpdate };
    case m.replace.SCHEDULE_UPDATE: return { scheduleUpdate: data.scheduleUpdate };
    case m.replace.SUGGESTION_UPDATE: return { suggestionUpdate: data.suggestionUpdate };
    // The requestId lets a history request recognize its own reply after the
    // reducer has merged it (REQUEST_ID is dispatched before CHAT_UPDATE).
    case m.special.CHAT_UPDATE: return { chatUpdate: data.chatUpdate, requestId: data.requestId };
    case m.special.MESSAGE_READ_INFO: return { messageReadInfo: data.messageReadInfo };
    case m.special.NEW_CHAT: return { newChat: data.newChat };
    case m.special.ERROR_MESSAGE: return { errorMsg: data.errorMsg };
    case m.special.REQUEST_ID: return {
      requestId: data.requestId,
      hasError: typeof data.errorMsg === "string" && data.errorMsg.length > 0,
    };
    case m.special.FILE_UPLOAD: return { uploadFile: data.uploadFile };
    case m.snapshot.EXPERT_SNAPSHOT: return { experts: data.experts };
    case m.snapshot.OFFER_SNAPSHOT: return { offers: data.offers };
    case m.snapshot.AGREEMENTS_SNAPSHOT: return { agreements: data.agreements };
    case m.replace.AGREEMENT_UPDATE: return { agreementUpdate: data.agreementUpdate };
    case m.special.TOXIC_REPLY: return { toxicReply: data.toxicReply };
    case m.special.LOG_ENTRIES: return { logEntries: data.logEntries };
    case m.snapshot.EVENT_SNAPSHOT: return { events: data.events };
    case m.special.EVENT_UPDATE: return { eventUpdate: data.eventUpdate };
    case m.snapshot.TODO_SNAPSHOT: return { todos: data.todos };
    case m.special.TODO_REMOVED: return { todoRemoved: data.todoRemoved };
    case m.replace.TODO_UPDATE: return { todoUpdate: data.todoUpdate };
    case m.replace.CALENDAR_SETTINGS: return { calendarSettings: data.calendarSettings };
    case m.special.ECO_AGREEMENT_UPDATE: return { ecoAgreements: data.ecoAgreements };
    case m.special.ECO_PURCHASE_UPDATE: return { ecoPurchases: data.ecoPurchases };
    case m.special.ECO_TRANSFER_UPDATE: return { ecoTransfers: data.ecoTransfers };
    case m.snapshot.RESOURCE_SNAPSHOT: return { resources: data.resources };
    case m.snapshot.ECO_SETTINGS_SNAPSHOT: return { ecoSettings: data.ecoSettings };
    case m.snapshot.SUBSCRIPTION_SNAPSHOT: return { subscriptions: data.subscriptions };
    case m.snapshot.RATINGS_SNAPSHOT: return { ratingDetails: data.ratingDetails };
    case m.special.READ_ARTICLES_UPDATE: return { readArticles: data.readArticles };
    case m.special.USER_TOKEN_UPDATE: return { userToken: data.userToken };
    case m.replace.STRICT_TOXIC_CHECK: return { strictToxicCheck: data.strictToxicCheck, toxicCheckLevel: data.toxicCheckLevel };
    // Sent without a value when the account follows the phone.
    case m.replace.DARK_MODE: return { darkMode: data.darkMode };
    // Sent only to parents who have set it, for the connected family.
    case m.replace.AUTO_APPROVE_SUGGESTIONS: return { autoApproveSuggestions: data.autoApproveSuggestions };
    case m.special.EVENT_ANALYZE_UPDATE: return { imageAnalysis: data.imageAnalysis };
    case m.snapshot.EXTERNAL_CALENDAR_SNAPSHOT: return { externalCalendars: data.externalCalendars };
    case m.replace.EXTERNAL_CALENDAR_UPDATE: return { externalCalendar: data.externalCalendar };
    case m.special.EXTERNAL_CALENDAR_DELETE: return { removeExternalCalendar: data.removeExternalCalendar};
    case m.special.AI_TASK: return { aiTask: data.aiTask};
    case m.special.AI_SCHEDULE_UPDATE: return { aiScheduleUpdate: data.aiScheduleUpdate};
    case m.replace.CALLS: return { calls: data.calls};
    case m.replace.CALL_DATA: return { callData: data.callData};
    default: return null;
  }
}

export function* bootstrap(action: any): SagaIterator {
  try {
    const { history } = action.payload?.meta || {};
    
    // Select necessary pieces from state
    const user:any = yield select((state: any) => state.user.session.data);
    const appToken:string | undefined = yield select((state: any) => state.user.appToken);

    if (user && user.authorization) {
      setAuthHeader(user.authorization);

      // The Gmail confirmation page needs authentication, but it deliberately
      // does not bootstrap the full calendar/WebSocket application. Keeping
      // this route isolated also avoids loading unrelated realtime services
      // while a one-time connection attempt is in memory.
      if (history && isGmailConnectPath(history.location.pathname)) {
        return;
      }
      
      const session: any = yield select((state: any) => state.user.session);
      const resources: any[] = Array.isArray(user.resources) ? user.resources : [];
      const selectedFamily: number = Number.isInteger(session?.selectedFamily) ? session.selectedFamily : 0;
      const selectedResource = resources[selectedFamily] || resources[0];

      if (!selectedResource?.familyURL) {
        logger.warn("Cannot bootstrap websocket without a family URL.");
        return;
      }

      const lastReconnect = session?.bootstrapTime;
      const now = Date.now();

      const delay = (lastReconnect && (now - lastReconnect < 2000)) ? 1000 : 0;
      yield put(UserActions.bootstrapTime(now));

      yield put(CommonActions.initWebsocket({
        socketURL: selectedResource.familyURL,
        subscribeData: { authorization: user.authorization },
        bootstrapTime: delay,
        appToken,
      }));

      // Navigation: Ensure authenticated user is on /app
      if (history) {
        const path = history.location.pathname;
        if (!path || !path.includes(privatRoutesPathes.APP)) {
          console.log("Navigating to /app for authenticated user.");
          history.replace(privatRoutesPathes.APP);
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* socketSaga(action: any): SagaIterator {
  // Use 'any' for the yield result, then cast to the return type of eventChannel
  const socketChannel: any = yield call(connectSocket, action.payload);

  try {
    while (true) {
      const socketAction = yield take(socketChannel);
      
      if (socketAction) {
        yield put(socketAction);
      }
    }
  } catch (err) {
    logger.error("Socket Saga stream error:", err);
  } finally {
    // Check if close exists before calling to satisfy 'never' or 'any' types
    if (socketChannel && typeof socketChannel.close === 'function') {
      socketChannel.close();
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/common/common.saga.ts